/**
 * 繰り返し支払い(旧: 固定費マスタ。2026-09-07 発注者指示で改修 / 2026-09-08 登録画面を経費申請と同じ作りに)。
 * 家賃・水道光熱費・リースなど、決まった周期で支払うものを登録する。
 * 各回は「支払い待ち」として自動で作られ、経理が「支払い方法を入力する」→ 承認者が「完了確認」でロックする。
 * 設定項目: 内容 / 金額(固定 or 変動=目安) / 支払方法・支払口座(銀行) / 繰り返し(毎月 or 毎週)・支払日 /
 *           計上日(支払日の前々月〜翌々月 + 日にち) / 土日祝の扱い / 期間 / 金額の改定(任意)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatCashAccountLabel } from '@core/lib/cashAccountLabel';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import MonthSelect from '@core/components/MonthSelect';
import { currentMonth, toMonthLabel } from '@core/lib/date';
import { formatAmountInput, formatYen, parseAmount } from '@core/lib/money';
import { fetchCashAccounts } from '@core/cash/cashRepo';
import { fetchUsers } from '@core/masters/masterRepo';
import { debitRuleLabel, debitShiftLabel, hasDebitRule } from '@core/lib/cardDebit';
import { fetchHierarchyNodes, toPathLabel } from '@core/masters/hierarchyRepo';
import {
  amountForMonth,
  createRecurringCost,
  deleteRecurringCost,
  fetchRecurringAuditLogs,
  fetchRecurringCosts,
  updateRecurringCost,
  type AmountRevisionInput,
  type RecurringCostInput,
} from './recurringRepo';
import { runRecurringHousekeeping } from './autoRecurring';
import { syncRecurringOccurrences } from './postRecurring';
import {
  POSTING_OFFSET_OPTIONS,
  WEEKDAY_OPTIONS,
  describeHolidayShift,
  describePaymentDay,
  describePostingDay,
  frequencyOf,
  holidayShiftOf,
  normalizeRules,
  type PostingOffset,
} from './schedule';
import {
  PAYMENT_METHOD_LABELS,
  type AuditLog,
  type CashAccount,
  type DayOrEnd,
  type DepartmentId,
  type HierarchyNode,
  type HolidayShift,
  type PaymentMethod,
  type RecurringCost,
} from '@core/lib/types';

/**
 * 繰り返し支払いで選べる支払方法(2026-09-07: 口座引落 or 振込 / 2026-09-08: クレジットカードを追加 /
 * 2026-10-20: 現金を追加。水道光熱費などで現金払いのものがあるため。振込と同じく毎月経理が支払い済みにする)
 */
const RECURRING_METHODS: PaymentMethod[] = ['direct_debit', 'bank_transfer', 'card', 'cash'];

/** 日時の表示(登録日・変更履歴) */
function fmtTs(ts: unknown, withTime = true): string {
  const d = ts && typeof (ts as { toDate?: () => Date }).toDate === 'function' ? (ts as { toDate: () => Date }).toDate() : null;
  if (!d) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
  return withTime ? `${date} ${p(d.getHours())}:${p(d.getMinutes())}` : date;
}

/** 日にち(1〜31 と 末日) */
const DAY_OPTIONS: Array<{ value: string; label: string }> = [
  ...Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}日` })),
  { value: 'end', label: '末日' },
];
const parseDay = (v: string): DayOrEnd => (v === 'end' ? 'end' : Number(v));

/** 「改定を追加」の1行(適用開始月と、その月からの金額) */
type RevisionRow = { month: string; amountText: string };

type FormState = Omit<RecurringCostInput, 'paymentRule' | 'postingRule' | 'frequency'> & {
  id: string | null;
  amountText: string;
  frequencyKind: 'monthly' | 'weekly';
  /** 毎週のときの曜日(0=日〜6=土) */
  weekday: number;
  /** 毎月の支払日: '1'〜'31' または 'end' */
  payDay: string;
  /** 計上日の月: 支払日の '-2'(前々月)〜'2'(翌々月) */
  postOffset: string;
  /** 計上日の日にち: '1'〜'31' または 'end' */
  postDay: string;
  revisions: RevisionRow[];
};

function emptyForm(companyId: string, departmentId: DepartmentId): FormState {
  return {
    id: null,
    name: '',
    companyId,
    departmentId,
    hierarchyNodeId: null,
    accountId: '',
    subAccountId: '',
    amount: 0,
    amountText: '',
    amountVaries: false,
    paymentMethod: 'direct_debit',
    cashAccountId: null,
    frequencyKind: 'monthly',
    weekday: 1,
    holidayShift: 'after',
    payDay: '5',
    postOffset: '-1',
    postDay: 'end',
    startMonth: currentMonth(),
    endMonth: null,
    counterparty: '',
    description: '',
    active: true,
    revisions: [],
  };
}

const Req = () => <span className="req">必須</span>;

export default function RecurringCostsPage() {
  const { user } = useAuth();
  const {
    departments,
    accounts,
    subAccountsOf,
    accountName,
    departmentName,
    companyName,
    companies,
    defaultCompanyId,
    fiscal,
  } = useMasters();
  const toast = useToast();

  const [costs, setCosts] = useState<RecurringCost[]>([]);
  const [cashAccounts, setCashAccounts] = useState<CashAccount[]>([]);
  const [nodes, setNodes] = useState<HierarchyNode[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  /**
   * 登録・編集画面はURL(?form=new / ?form=<id>)で開く(2026-09-09 指摘: 画面上部の「戻る」で一覧に戻れるように)。
   * 履歴に1つ積まれるので、ヘッダーの「戻る」(history back)が一覧に戻る
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const formParam = searchParams.get('form');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  /** 登録者・変更者の表示名(2026-09-08: 登録日と変更履歴の表示) */
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  /** 編集中の繰り返し支払いの変更履歴(監査ログ) */
  const [history, setHistory] = useState<AuditLog[]>([]);
  const nameOf = (id: string | null | undefined) => (id ? (userNames.get(id) ?? id) : '—');

  /**
   * 科目①の候補(2026-09-28 指示で修正)。判定は経費申請とまったく同じにする:
   * マスタ「表示する事業部」にこの事業部が入っている科目を出す(「その他」事業部は全部出す)。
   * 以前は「PL区分=経費」だけに絞っていたため、原価の科目(仕入・外注費・業務委託費・法定福利費・
   * 給料手当)とPL区分未設定の科目(積立金・預かり金)が候補に出てこなかった。
   * 編集中の設定に入っている科目は、候補から外れていても選択肢に残す(黙って消さない)
   */
  const expenseAccounts = useMemo(() => {
    const departmentId = form?.departmentId;
    const shown = accounts.filter(
      (a) =>
        a.active &&
        (a.department_ids?.length ?? 0) > 0 &&
        (departmentId === 'sonota' || (!!departmentId && a.department_ids!.includes(departmentId))),
    );
    const current = form?.accountId ? accounts.find((a) => a.id === form.accountId) : undefined;
    const list = current && !shown.some((a) => a.id === current.id) ? [...shown, current] : shown;
    return [...list].sort((a, b) => a.display_order - b.display_order);
  }, [accounts, form?.departmentId, form?.accountId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, cash, users] = await Promise.all([
        fetchRecurringCosts(),
        fetchCashAccounts().catch(() => [] as CashAccount[]),
        fetchUsers().catch(() => []),
      ]);
      setCosts(list);
      setCashAccounts(cash);
      setUserNames(new Map(users.map((u) => [u.id, u.display_name])));
    } catch (e) {
      toast(e instanceof Error ? e.message : '繰り返し支払いを取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // フォームの事業部が変わったら階層の選択肢を読み直す
  useEffect(() => {
    if (!form) return;
    let alive = true;
    fetchHierarchyNodes(form.departmentId)
      .then((list) => alive && setNodes(list))
      .catch(() => alive && setNodes([]));
    return () => {
      alive = false;
    };
  }, [form?.departmentId]);

  function startCreate() {
    setSearchParams({ form: 'new' });
  }

  function startEdit(cost: RecurringCost) {
    setSearchParams({ form: cost.id });
  }

  /** 一覧へ戻る(履歴があれば1つ戻る=URLから ?form が消える。直接開いたときは一覧へ置き換え) */
  function closeForm() {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate('/recurring', { replace: true });
  }

  function buildForm(cost: RecurringCost): FormState {
    const freq = frequencyOf(cost);
    const r = normalizeRules(cost);
    return {
      id: cost.id,
      name: cost.name,
      companyId: cost.company_id ?? defaultCompanyId,
      departmentId: cost.department_id,
      hierarchyNodeId: cost.hierarchy_node_id,
      accountId: cost.account_id,
      subAccountId: cost.sub_account_id ?? '',
      amount: cost.amount,
      amountText: formatAmountInput(String(cost.amount)),
      amountVaries: cost.amount_varies === true,
      paymentMethod: cost.payment_method,
      cashAccountId: cost.cash_account_id,
      frequencyKind: freq.kind,
      weekday: freq.kind === 'weekly' ? freq.weekday : 1,
      holidayShift: holidayShiftOf(cost),
      payDay: String(r.payDay),
      postOffset: String(r.postOffset),
      postDay: String(r.postDay),
      startMonth: cost.start_month,
      endMonth: cost.end_month,
      counterparty: cost.counterparty ?? '',
      description: cost.description ?? '',
      // 状態(有効/無効)の機能は廃止(2026-09-09 指示)。期間(開始月・終了月)で管理する
      active: true,
      revisions: [],
    };
  }

  // URL(?form=)に合わせてフォームを開閉する。一覧の再読込ではフォームを作り直さない
  useEffect(() => {
    if (!formParam) {
      setForm(null);
      return;
    }
    if (formParam === 'new') {
      setForm((prev) => (prev && prev.id === null ? prev : emptyForm(defaultCompanyId, (departments[0]?.id ?? 'honbu') as DepartmentId)));
      setHistory([]);
      window.scrollTo({ top: 0 });
      return;
    }
    const cost = costs.find((c) => c.id === formParam);
    if (!cost) return; // 一覧の読み込み待ち(見つからなければ一覧のまま)
    setForm((prev) => {
      if (prev && prev.id === cost.id) return prev;
      setHistory([]);
      fetchRecurringAuditLogs(cost.id)
        .then(setHistory)
        .catch(() => setHistory([]));
      window.scrollTo({ top: 0 });
      return buildForm(cost);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formParam, costs, defaultCompanyId, departments]);

  /** 変更内容を文章にする(変更履歴に残す・2026-09-08) */
  function describeChanges(current: RecurringCost, input: RecurringCostInput, revisions: AmountRevisionInput[]): string[] {
    const out: string[] = [];
    const acct = (id: string | null) => {
      const a = id ? cashAccounts.find((x) => x.id === id) : undefined;
      return a ? formatCashAccountLabel(a, companyName(a.company_id)) : id ? id : '未設定';
    };
    const sub = (accountId: string, id: string) => (id ? (subAccountsOf(accountId).find((s) => s.id === id)?.name ?? id) : '');
    const pair = (label: string, a: string, b: string) => {
      if (a !== b) out.push(`${label}: ${a || '—'} → ${b || '—'}`);
    };
    const next = {
      ...current,
      payment_method: input.paymentMethod,
      payment_rule: input.paymentRule,
      posting_rule: input.postingRule,
      frequency: input.frequency,
      holiday_shift: input.holidayShift,
    } as RecurringCost;
    pair('内容', current.name, input.name.trim());
    pair('会社', companyName(current.company_id ?? defaultCompanyId), companyName(input.companyId));
    pair('事業部', departmentName(current.department_id), departmentName(input.departmentId));
    pair('科目①', accountName(current.account_id), accountName(input.accountId));
    pair('科目②', sub(current.account_id, current.sub_account_id ?? ''), sub(input.accountId, input.subAccountId));
    pair('固定／変動', current.amount_varies ? '変動' : '固定', input.amountVaries ? '変動' : '固定');
    pair('金額', `${formatYen(current.amount)}円`, `${formatYen(input.amount)}円`);
    pair('支払方法', PAYMENT_METHOD_LABELS[current.payment_method], PAYMENT_METHOD_LABELS[input.paymentMethod]);
    pair('支払口座', acct(current.cash_account_id), acct(input.cashAccountId));
    pair('支払日', describePaymentDay(current), describePaymentDay(next));
    pair('計上日', describePostingDay(current), describePostingDay(next));
    if (input.paymentMethod !== 'card') pair('土日祝', describeHolidayShift(holidayShiftOf(current)), describeHolidayShift(input.holidayShift));
    pair('開始月', toMonthLabel(current.start_month), toMonthLabel(input.startMonth));
    pair('終了月', current.end_month ? toMonthLabel(current.end_month) : '無期限', input.endMonth ? toMonthLabel(input.endMonth) : '無期限');
    pair('支払先', current.counterparty ?? '', input.counterparty.trim());
    pair('摘要', current.description ?? '', input.description.trim());
    for (const r of revisions) out.push(`改定を追加: ${toMonthLabel(r.effectiveFrom)}から ${formatYen(r.amount)}円`);
    return out;
  }

  /** 変更履歴の1行の文章 */
  function historyText(h: AuditLog): string {
    const changes = (h.after as { changes?: unknown } | null)?.changes;
    if (Array.isArray(changes) && changes.length > 0) return changes.map(String).join(' ／ ');
    if (h.action === 'recurring-cost-create') return '登録';
    if (h.action === 'recurring-cost-delete') return '削除';
    if (h.action === 'recurring-cost-activate') return '有効にした';
    if (h.action === 'recurring-cost-deactivate') return '無効にした';
    const b = (h.before as { amount?: number } | null)?.amount;
    const a = (h.after as { amount?: number } | null)?.amount;
    if (h.action === 'recurring-cost-amount-change' && typeof b === 'number' && typeof a === 'number') {
      return `金額: ${formatYen(b)}円 → ${formatYen(a)}円`;
    }
    return '設定を変更(内容の記録なし)';
  }

  async function save() {
    if (!form || !user) return;
    if (!form.name.trim()) {
      toast('内容(名称)を入力してください');
      return;
    }
    if (!form.accountId) {
      toast('科目①を選んでください');
      return;
    }
    const amount = parseAmount(form.amountText);
    if (amount === null) {
      toast(form.amountVaries ? '目安の金額を入力してください(1円以上の整数)' : '金額を正しく入力してください(1円以上の整数)');
      return;
    }
    if (!form.cashAccountId) {
      toast(
        form.paymentMethod === 'card'
          ? 'カードを選んでください'
          : form.paymentMethod === 'cash'
            ? '支払口座(現金)を選んでください'
            : '支払口座(引き落とし・振込元の口座)を選んでください',
      );
      return;
    }
    if (!form.counterparty.trim()) {
      toast('支払先を入力してください');
      return;
    }
    if (!form.startMonth) {
      toast('開始月を選んでください');
      return;
    }
    const revisions: AmountRevisionInput[] = [];
    for (const r of form.revisions) {
      if (!r.month && !r.amountText.trim()) continue; // 空の行は無視
      const a = parseAmount(r.amountText);
      if (!r.month || a === null) {
        toast('金額の改定は、適用開始月と金額の両方を入れてください');
        return;
      }
      revisions.push({ amount: a, effectiveFrom: r.month });
    }

    const input: RecurringCostInput = {
      name: form.name,
      companyId: form.companyId,
      departmentId: form.departmentId,
      hierarchyNodeId: form.hierarchyNodeId,
      accountId: form.accountId,
      subAccountId: form.subAccountId,
      amount,
      amountVaries: form.amountVaries,
      paymentMethod: form.paymentMethod,
      cashAccountId: form.cashAccountId,
      frequency: form.frequencyKind === 'weekly' ? { kind: 'weekly', weekday: form.weekday } : { kind: 'monthly' },
      holidayShift: form.holidayShift,
      // クレカは計上日(PL)の日にちがカードの利用日(2026-09-09 指示)。支払日欄は使わない
      paymentRule: { kind: 'day_of_month', day: parseDay(form.paymentMethod === 'card' ? form.postDay : form.payDay) },
      postingRule: {
        kind: 'relative',
        month_offset: form.paymentMethod === 'card' ? 0 : (Number(form.postOffset) as PostingOffset),
        day: parseDay(form.postDay),
      },
      startMonth: form.startMonth,
      endMonth: form.endMonth,
      counterparty: form.counterparty,
      description: form.description,
      active: form.active,
    };

    setBusy(true);
    try {
      if (form.id) {
        const current = costs.find((c) => c.id === form.id);
        if (!current) throw new Error('対象が見つかりません');
        // 金額欄を直接変えた場合は今月から反映。先の月からの変更は「改定を追加」で入れる
        await updateRecurringCost({
          id: form.id,
          input,
          current,
          effectiveFrom: currentMonth(),
          userId: user.id,
          revisions,
          changes: describeChanges(current, input, revisions),
        });
        let msg = revisions.length > 0 ? '繰り返し支払いを更新し、金額の改定を登録しました' : '繰り返し支払いを更新しました';
        // 変更を、まだ支払っていない支払い待ちにも反映し、新たに必要になった回を作る(2026-09-08 指摘対応)
        try {
          const after = (await fetchRecurringCosts()).find((c) => c.id === form.id);
          if (after) {
            const s = await syncRecurringOccurrences({
              cost: after,
              userId: user.id,
              fiscalStartMonth: fiscal.start_month,
              accounts: cashAccounts,
            });
            const r = await runRecurringHousekeeping({ userId: user.id, fiscalStartMonth: fiscal.start_month, force: true });
            const parts = [
              s.updated > 0 ? `支払い待ち${s.updated}件を新しい設定に合わせました` : '',
              s.cancelled > 0 ? `期間外になった${s.cancelled}件を取消にしました` : '',
              r && r.created > 0 ? `支払い待ちを${r.created}件作りました` : '',
              r && r.notified > 0 ? '支払日が近い分は経理へお知らせしました' : '',
            ].filter(Boolean);
            if (parts.length > 0) msg += `(${parts.join('・')})`;
          }
        } catch (e) {
          msg += `。ただし支払い待ちへの反映に失敗しました: ${e instanceof Error ? e.message : String(e)}`;
        }
        toast(msg);
      } else {
        await createRecurringCost(input, user.id, revisions);
        // 登録した直後に、当月〜翌々月に支払う分の支払い待ちを作る(支払日が近ければ経理へお知らせ)
        try {
          const r = await runRecurringHousekeeping({ userId: user.id, fiscalStartMonth: fiscal.start_month, force: true });
          toast(
            r === null
              ? '繰り返し支払いを登録しました(自動処理がOFFのため支払い待ちは作られません。マスタ管理→設定で確認してください)'
              : r.created > 0
                ? `繰り返し支払いを登録し、支払い待ちを${r.created}件作りました${r.notified > 0 ? '(支払日が近い分は経理へお知らせしました)' : ''}`
                : '繰り返し支払いを登録しました(この期間に支払う分はありません)',
          );
        } catch (e) {
          toast(`繰り返し支払いを登録しましたが、支払い待ちの作成に失敗しました: ${e instanceof Error ? e.message : String(e)}`);
        }
      }
      await load();
      closeForm();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function remove(cost: RecurringCost) {
    if (!user) return;
    if (!window.confirm(`「${cost.name}」を削除します。作成済みの支払い待ち・支払い済みの取引は残ります。よろしいですか?`)) return;
    try {
      await deleteRecurringCost(cost.id, user.id);
      await load();
      toast('削除しました(作成済みの取引は残ります)');
    } catch (e) {
      toast(e instanceof Error ? e.message : '削除に失敗しました');
    }
  }

  /**
   * 支払口座の候補(2026-09-08 指示): 口座引落・振込は選んだ会社の銀行口座、クレジットカードは選んだ会社のカード、
   * 現金は選んだ会社の現金(2026-10-20 追加)。会社に無ければ全社から
   */
  const accountOptions = (companyId: string, method: PaymentMethod) => {
    const kind = method === 'card' ? 'card' : method === 'cash' ? 'cash' : 'bank';
    const all = cashAccounts.filter((a) => a.active && a.kind === kind);
    const mine = all.filter((a) => a.company_id === companyId);
    return mine.length > 0 ? mine : all;
  };

  /** 支払方法の選択肢: 口座引落・振込・クレジットカード・現金。古いデータの別の方法は選択肢に残す */
  const methodOptions = (current: PaymentMethod) =>
    RECURRING_METHODS.includes(current) ? RECURRING_METHODS : [...RECURRING_METHODS, current];

  const editingCost = form?.id ? costs.find((c) => c.id === form.id) : undefined;

  // ---- 登録・編集画面(経費申請と同じ作り: 2列・必須の印・同じ並び) ----
  if (form) {
    const weekly = form.frequencyKind === 'weekly';
    const card = form.paymentMethod === 'card';
    const selectedCard = card && form.cashAccountId ? (cashAccounts.find((a) => a.id === form.cashAccountId) ?? null) : null;
    return (
      <section className="screen">
        <div className="card" style={{ maxWidth: 760 }}>
          <h3 style={{ display: 'flex', alignItems: 'center' }}>
            {form.id ? '繰り返し支払いを編集する' : '繰り返し支払いを登録する'}
            <button
              className="txn-link"
              style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 400, color: 'var(--muted)' }}
              onClick={closeForm}
              disabled={busy}
            >
              一覧に戻る
            </button>
          </h3>
          {editingCost && (
            <p className="hint" style={{ margin: '-6px 0 12px' }}>
              登録日: {fmtTs(editingCost.created_at)} {nameOf(editingCost.created_by)} ／ 最終更新: {fmtTs(editingCost.updated_at)}{' '}
              {nameOf(editingCost.updated_by)}
            </p>
          )}

          <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <div className="fw">
              <label>
                内容(名称)<Req />
              </label>
              <input
                autoFocus
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="例)品川オフィス家賃 / 電気代"
              />
            </div>
            <div>
              <label>申請者</label>
              <input readOnly disabled value="" style={{ background: '#EEF2F8', color: 'var(--muted)' }} />
            </div>
            <div>
              <label>
                会社<Req />
              </label>
              <select
                value={form.companyId}
                onChange={(e) => setForm({ ...form, companyId: e.target.value, cashAccountId: null })}
              >
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>
                事業部<Req />
              </label>
              <select
                value={form.departmentId}
                onChange={(e) => setForm({ ...form, departmentId: e.target.value, hierarchyNodeId: null })}
              >
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>拠点/場所(任意)</label>
              <select
                value={form.hierarchyNodeId ?? ''}
                disabled={nodes.length === 0}
                onChange={(e) => setForm({ ...form, hierarchyNodeId: e.target.value || null })}
              >
                <option value="">{nodes.length === 0 ? '(階層マスタ未登録)' : '—'}</option>
                {nodes.map((n) => (
                  <option key={n.id} value={n.id}>
                    {toPathLabel(n, nodes)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>
                科目①<Req />
              </label>
              <select
                value={form.accountId}
                onChange={(e) => setForm({ ...form, accountId: e.target.value, subAccountId: '' })}
              >
                <option value="">選択してください</option>
                {expenseAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>科目②(任意)</label>
              <select
                value={form.subAccountId}
                disabled={!form.accountId}
                onChange={(e) => setForm({ ...form, subAccountId: e.target.value })}
              >
                <option value="">—</option>
                {(form.accountId ? subAccountsOf(form.accountId) : []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label>
                固定／変動<Req />
              </label>
              <select
                value={form.amountVaries ? 'varies' : 'fixed'}
                onChange={(e) => setForm({ ...form, amountVaries: e.target.value === 'varies' })}
              >
                <option value="fixed">固定</option>
                <option value="varies">変動</option>
              </select>
            </div>
            <div>
              <label>
                {form.amountVaries ? '目安の金額(円)' : '金額(円)'}
                <Req />
              </label>
              <input
                inputMode="numeric"
                placeholder="462,000"
                value={form.amountText}
                onChange={(e) => setForm({ ...form, amountText: formatAmountInput(e.target.value) })}
              />
            </div>

            <div>
              <label>
                支払方法<Req />
              </label>
              <select
                value={form.paymentMethod}
                onChange={(e) =>
                  setForm({
                    ...form,
                    paymentMethod: e.target.value as PaymentMethod,
                    // 口座の種類が変わる(銀行・カード・現金)ので選び直してもらう
                    cashAccountId: null,
                    // 銀行の引き落としは翌営業日、こちらから振り込む・現金で払うときは前の営業日が一般的
                    holidayShift: e.target.value === 'bank_transfer' || e.target.value === 'cash' ? 'before' : 'after',
                  })
                }
              >
                {methodOptions(form.paymentMethod).map((m) => (
                  <option key={m} value={m}>
                    {PAYMENT_METHOD_LABELS[m]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>
                {card ? 'カード' : form.paymentMethod === 'cash' ? '支払口座(現金)' : '支払口座(引き落とし・振込元)'}
                <Req />
              </label>
              <select
                value={form.cashAccountId ?? ''}
                onChange={(e) => setForm({ ...form, cashAccountId: e.target.value || null })}
              >
                <option value="">選択してください</option>
                {accountOptions(form.companyId, form.paymentMethod).map((a) => (
                  <option key={a.id} value={a.id}>
                    {formatCashAccountLabel(a, companyName(a.company_id))}
                  </option>
                ))}
              </select>
              {card && selectedCard && (
                <div className="hint" style={{ marginTop: 3 }}>
                  {hasDebitRule(selectedCard)
                    ? `${debitRuleLabel(selectedCard)}・${debitShiftLabel(selectedCard.debit_holiday_shift)}`
                    : 'このカードは締め日・引き落とし日が未設定です。マスタ管理→支払い方法で設定してください(設定まで引き落とし日=利用日で作られます)'}
                </div>
              )}
            </div>

            {/* 繰り返し / 支払日 → 計上日(支払日の前々月〜翌々月) / 計上日の日にち(2026-09-08 指示の並び) */}
            <div>
              <label>
                繰り返し<Req />
              </label>
              <select
                value={form.frequencyKind}
                onChange={(e) => setForm({ ...form, frequencyKind: e.target.value as FormState['frequencyKind'] })}
              >
                <option value="monthly">毎月</option>
                <option value="weekly">毎週</option>
              </select>
            </div>
            {/* クレカ(2026-09-09 指示): 支払日欄は出さない。計上日(PL)の日にちがカードの利用日で、CFはカードの引き落とし日(自動) */}
            {!card && (
              <div>
                <label>
                  支払日(CF)<Req />
                </label>
                {weekly ? (
                  <select value={form.weekday} onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })}>
                    {WEEKDAY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select value={form.payDay} onChange={(e) => setForm({ ...form, payDay: e.target.value })}>
                    {DAY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
            {!card && (
              <div>
                <label>
                  計上日(PL)<Req />
                </label>
                <select
                  value={form.postOffset}
                  disabled={weekly}
                  onChange={(e) => setForm({ ...form, postOffset: e.target.value })}
                >
                  {POSTING_OFFSET_OPTIONS.map((o) => (
                    <option key={o.value} value={String(o.value)}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {weekly && <div className="hint" style={{ marginTop: 3 }}>毎週の分は支払日と同じ日に計上します</div>}
              </div>
            )}
            <div>
              <label>
                {card ? '計上日(PL)' : '計上日(PL) — 日にち'}
                <Req />
              </label>
              {card && weekly ? (
                <select value={form.weekday} onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })}>
                  {WEEKDAY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <select value={form.postDay} disabled={weekly && !card} onChange={(e) => setForm({ ...form, postDay: e.target.value })}>
                  {DAY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              )}
              {card && <div className="hint" style={{ marginTop: 3 }}>この日にカードで払った分として計上します。CF(支払日)はカードの引き落とし日になります(自動)</div>}
            </div>

            <div>
              <label>
                支払日が土日祝のとき<Req />
              </label>
              {card ? (
                <input readOnly disabled value={selectedCard ? debitShiftLabel(selectedCard.debit_holiday_shift) : 'カードの設定に従う'} style={{ background: '#EEF2F8', color: 'var(--muted)' }} />
              ) : (
                <select
                  value={form.holidayShift}
                  onChange={(e) => setForm({ ...form, holidayShift: e.target.value as HolidayShift })}
                >
                  <option value="before">前の平日にする</option>
                  <option value="after">後の平日にする</option>
                  <option value="none">そのまま</option>
                </select>
              )}
            </div>
            <div />
            <div>
              <label>
                開始月(最初に支払う月)<Req />
              </label>
              <MonthSelect value={form.startMonth} onChange={(v) => setForm({ ...form, startMonth: v })} />
            </div>
            <div>
              <label>終了月(任意・最後に支払う月)</label>
              <MonthSelect
                value={form.endMonth ?? ''}
                onChange={(v) => setForm({ ...form, endMonth: v || null })}
                allowEmpty
                emptyLabel="無期限"
                yearsAhead={5}
              />
            </div>

            {/* 金額の改定(任意・2026-09-08 指示): 1行=適用開始月と金額。＋で行を足す */}
            <div className="fw">
              <label>金額の改定(任意) — 先の月から金額が変わるとき(月は計上月=「○月分」)</label>
              {editingCost && (editingCost.amount_history ?? []).length > 1 && (
                <div className="hint" style={{ marginBottom: 6 }}>
                  これまでの改定:{' '}
                  {(editingCost.amount_history ?? [])
                    .map((h) => `${toMonthLabel(h.effective_from)}〜 ${formatYen(h.amount)}円`)
                    .join(' / ')}
                </div>
              )}
              {form.revisions.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <MonthSelect
                      value={r.month}
                      onChange={(v) =>
                        setForm({ ...form, revisions: form.revisions.map((x, j) => (j === i ? { ...x, month: v } : x)) })
                      }
                      yearsAhead={5}
                    />
                  </div>
                  <input
                    inputMode="numeric"
                    placeholder="改定後の金額(円)"
                    style={{ flex: '1 1 160px', minWidth: 0 }}
                    value={r.amountText}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        revisions: form.revisions.map((x, j) =>
                          j === i ? { ...x, amountText: formatAmountInput(e.target.value) } : x,
                        ),
                      })
                    }
                  />
                  <button
                    className="txn-link"
                    onClick={() => setForm({ ...form, revisions: form.revisions.filter((_, j) => j !== i) })}
                  >
                    取消
                  </button>
                </div>
              ))}
              <button
                className="btn-line"
                style={{ padding: '6px 14px' }}
                onClick={() => setForm({ ...form, revisions: [...form.revisions, { month: '', amountText: '' }] })}
              >
                ＋ 改定を追加
              </button>
            </div>

            <div className="fw">
              <label>
                支払先<Req />
              </label>
              <input
                value={form.counterparty}
                onChange={(e) => setForm({ ...form, counterparty: e.target.value })}
                placeholder="例)〇〇不動産"
              />
            </div>
            <div className="fw">
              <label>摘要(任意)</label>
              <input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="補足があれば記入"
              />
            </div>
          </div>

          {editingCost && (
            <div className="gray-box" style={{ marginTop: 14 }}>
              <div className="box-title">変更履歴</div>
              {history.length === 0 ? (
                <p className="empty" style={{ padding: '6px 0' }}>変更履歴はまだありません。</p>
              ) : (
                <table className="no-wrap-table">
                  <thead>
                    <tr>
                      <th>日時</th>
                      <th>変更者</th>
                      <th>内容</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id}>
                        <td>{fmtTs(h.at)}</td>
                        <td>{nameOf(h.by)}</td>
                        <td style={{ whiteSpace: 'normal' }}>{historyText(h)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="modal-actions">
            <button className="btn-green" onClick={() => void save()} disabled={busy}>
              {busy ? '保存中…' : form.id ? '保存する' : '登録する'}
            </button>
            <button className="btn-line" onClick={closeForm} disabled={busy}>
              キャンセル
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ---- 一覧(固定と変動で枠を分ける・1行表示。2026-09-08 指示) ----
  /** 土日祝の列: クレカはカードの設定(マスタ管理→支払い方法)を出す */
  const shortShift = (cost: RecurringCost) => {
    const label =
      cost.payment_method === 'card'
        ? debitShiftLabel(cashAccounts.find((a) => a.id === cost.cash_account_id)?.debit_holiday_shift)
        : describeHolidayShift(holidayShiftOf(cost));
    return label.replace('土日祝は', '').replace('土日祝も', '');
  };
  const periodLabel = (cost: RecurringCost) =>
    `${cost.start_month.replace('-', '/')}〜${cost.end_month ? cost.end_month.replace('-', '/') : ''}`;
  const renderTable = (list: RecurringCost[], varies: boolean) =>
    list.length === 0 ? (
      <p className="empty">{varies ? '変動の繰り返し支払いはありません。' : '固定の繰り返し支払いはありません。'}</p>
    ) : (
      <table className="no-wrap-table compact">
        <thead>
          <tr>
            <th>内容</th>
            <th>会社/事業部</th>
            <th>科目</th>
            <th className="num">{varies ? '目安の金額' : '金額'}</th>
            <th>支払方法</th>
            <th>支払口座</th>
            <th>支払日</th>
            <th>計上日</th>
            <th>土日祝</th>
            <th>期間</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {list.map((cost) => {
            const acct = cost.cash_account_id ? cashAccounts.find((x) => x.id === cost.cash_account_id) : undefined;
            return (
              <tr key={cost.id}>
                <td title={cost.name}>{cost.name}</td>
                <td className="hint">
                  {companyName(cost.company_id ?? defaultCompanyId)} / {departmentName(cost.department_id)}
                </td>
                <td>{accountName(cost.account_id)}</td>
                <td className="num">{formatYen(amountForMonth(cost, currentMonth()))}</td>
                <td>{PAYMENT_METHOD_LABELS[cost.payment_method]}</td>
                <td className="hint">
                  {acct ? (
                    formatCashAccountLabel(acct, companyName(acct.company_id))
                  ) : (
                    <span className="badge b-pend" title="編集して支払口座を選び直してください">口座未設定</span>
                  )}
                </td>
                <td>{describePaymentDay(cost)}</td>
                <td className="hint">{describePostingDay(cost)}</td>
                <td className="hint">{shortShift(cost)}</td>
                <td className="hint">{periodLabel(cost)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="txn-link" onClick={() => startEdit(cost)}>
                    編集
                  </button>
                  <button className="txn-link" onClick={() => void remove(cost)}>
                    削除
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  const fixedCosts = costs.filter((c) => c.amount_varies !== true);
  const variableCosts = costs.filter((c) => c.amount_varies === true);

  return (
    <section className="screen" style={{ maxWidth: 1400 }}>
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          繰り返し支払い
          <button className="btn-line" style={{ marginLeft: 'auto' }} onClick={startCreate}>
            新規登録
          </button>
        </h3>
      </div>

      <div className="card">
        <h3>固定(毎回同じ金額)</h3>
        {loading ? <p className="empty">読み込み中…</p> : renderTable(fixedCosts, false)}
      </div>

      <div className="card">
        <h3>変動(毎回金額が違う — 支払い時に確定金額を入力)</h3>
        {loading ? <p className="empty">読み込み中…</p> : renderTable(variableCosts, true)}
      </div>

    </section>
  );
}
